// biome-ignore lint/style/noRestrictedImports: <AI generated code>
import { useFlags as originalUseFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';

import { useDevModal } from '~/components/DevModal/hooks/useDevModal';

type FlagValue = boolean | number | string | Record<string, unknown> | unknown[];

/** After the first client hydration pass, new `useFlags` mounts may read localStorage immediately. */
let hasHydratedDevFlagOverrides = false;

const devFlagHydrationListeners = new Set<() => void>();

const subscribeDevFlagHydration = (onStoreChange: () => void) => {
  devFlagHydrationListeners.add(onStoreChange);
  return () => {
    devFlagHydrationListeners.delete(onStoreChange);
  };
};

const getDevFlagHydrationSnapshot = () => hasHydratedDevFlagOverrides;

const getDevFlagHydrationServerSnapshot = () => false;

const markDevFlagOverridesHydrated = () => {
  if (hasHydratedDevFlagOverrides) {
    return;
  }

  hasHydratedDevFlagOverrides = true;

  for (const listener of devFlagHydrationListeners) {
    listener();
  }
};

/** @internal Test helpers for hydration latch behavior (not for production). */
export const devFlagOverrideHydrationForTests = {
  reset: () => {
    hasHydratedDevFlagOverrides = false;
    devFlagHydrationListeners.clear();
  },
  isHydrated: () => hasHydratedDevFlagOverrides,
  markHydrated: markDevFlagOverridesHydrated,
};

/** Rolled out in prod — default on in `next dev` so local SSR matches typical dev overrides. */
const NEXT_DEV_FLAG_DEFAULTS: Record<string, FlagValue> = {
  ui2026: true,
};

const getEnvironmentDevFlagDefaults = (): Record<string, FlagValue> => {
  if (process.env.NEXT_PUBLIC_LOCALDEV === 'true') {
    return {
      ...NEXT_DEV_FLAG_DEFAULTS,
      generativeCanvasEnabled: true,
    };
  }

  if (process.env.NODE_ENV === 'development') {
    return NEXT_DEV_FLAG_DEFAULTS;
  }

  return {};
};

const readDevFlagOverridesFromStorage = (): Record<string, FlagValue> => {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const overridesString = localStorage.getItem('dev-flag-overrides');

    if (!overridesString) {
      return {};
    }

    const overrides = JSON.parse(overridesString);

    return typeof overrides === 'object' && overrides !== null ? overrides : {};
  } catch (error) {
    console.error('Failed to parse dev flag overrides:', error);
    return {};
  }
};

/**
 * Dev flag overrides from localStorage plus env defaults (`next dev` / localdev).
 */
export const getDevFlagOverrides = ({
  readLocalStorage = true,
}: {
  readLocalStorage?: boolean;
} = {}): Record<string, FlagValue> => {
  const fromStorage = readLocalStorage ? readDevFlagOverridesFromStorage() : {};
  const envDefaults = getEnvironmentDevFlagDefaults();

  return { ...envDefaults, ...fromStorage };
};

export const applyDevFlagOverrides = <T extends Record<string, FlagValue>>(
  originalFlags: T,
  overrides: Record<string, FlagValue>,
): T => {
  if (Object.keys(overrides).length === 0) {
    return originalFlags;
  }

  return { ...originalFlags, ...overrides };
};

/**
 * Intercepted version of useFlags that applies dev overrides before returning
 */
export const useFlags = <T extends Record<string, FlagValue>>(): T => {
  const originalFlags = originalUseFlags<T>();
  const { registerDevFlagListener } = useDevModal();
  const [_overrideKey, setOverrideKey] = useState(0);
  const canReadDevFlagStorage = useSyncExternalStore(
    subscribeDevFlagHydration,
    getDevFlagHydrationSnapshot,
    getDevFlagHydrationServerSnapshot,
  );

  useEffect(() => {
    const unregister = registerDevFlagListener(() => {
      setOverrideKey((prev) => prev + 1);
    });

    return unregister;
  }, [registerDevFlagListener]);

  useEffect(() => {
    markDevFlagOverridesHydrated();

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'dev-flag-overrides') {
        setOverrideKey((prev) => prev + 1);
      }
    };

    const handleCustomEvent = () => {
      setOverrideKey((prev) => prev + 1);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('dev-flag-override-changed', handleCustomEvent);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('dev-flag-override-changed', handleCustomEvent);
    };
  }, []);

  return useMemo(() => {
    void _overrideKey;
    return applyDevFlagOverrides(originalFlags, getDevFlagOverrides({ readLocalStorage: canReadDevFlagStorage }));
  }, [originalFlags, _overrideKey, canReadDevFlagStorage]);
};
